import type { InvoiceSortParam } from '@features/invoices/api/get-invoices/getInvoices.types';

import { useInvoicesFilter } from '@features/invoices/contexts/filter/useInvoicesFilter';
import { useInvoices } from '@features/invoices/hooks/use-invoices/useInvoices';
import * as React from 'react';

import type {
  InvoicesDataContextValue,
  InvoicesDataProviderProps,
  InvoicesQueryState,
} from './InvoicesDataContext.types';

import {
  INITIAL_INVOICES_DATA,
  INITIAL_PAGE_INDEX,
  INITIAL_PAGE_SIZE,
} from './InvoicesDataProvider.utils';

const InvoicesDataContext = React.createContext<
  InvoicesDataContextValue | undefined
>(undefined);

const InvoicesDataProvider = React.memo(
  ({ children }: Readonly<InvoicesDataProviderProps>): React.ReactElement => {
    const [sort, setSort] = React.useState<InvoiceSortParam | undefined>(
      undefined
    );
    const [pageSize, setPageSize] = React.useState<number>(INITIAL_PAGE_SIZE);
    const [pageIndex, setPageIndex] =
      React.useState<number>(INITIAL_PAGE_INDEX);
    const { filters } = useInvoicesFilter();

    const handlePageIndexChange = React.useCallback(
      (newPageIndex: number): void => {
        setPageIndex(newPageIndex);
      },
      []
    );

    const handlePageSizeChange = React.useCallback(
      (newPageSize: number): void => {
        setPageSize(newPageSize);
        setPageIndex(0);
      },
      []
    );

    const handleSetSort = React.useCallback(
      (newSort: InvoiceSortParam | undefined): void => {
        setSort(newSort);
        setPageIndex(0);
      },
      []
    );

    const invoicesQuery = useInvoices({
      sort,
      pageSize,
      pageIndex,
      searchText: filters.searchText,
      dueDateStart: filters.dueDateStart,
      dueDateEnd: filters.dueDateEnd,
      issuedDateStart: filters.issuedDateStart,
      issuedDateEnd: filters.issuedDateEnd,
      balanceStatuses: filters.balanceStatuses,
      currencies: filters.currencies,
      platforms: filters.platforms,
      locations: filters.locations,
    });

    const invoices: InvoicesQueryState = React.useMemo(
      () => ({
        data: invoicesQuery.data ?? INITIAL_INVOICES_DATA,
        isLoading: invoicesQuery.isLoading,
        isError: invoicesQuery.isError,
        error: invoicesQuery.error,
      }),
      [
        invoicesQuery.data,
        invoicesQuery.isLoading,
        invoicesQuery.isError,
        invoicesQuery.error,
      ]
    );

    const contextValue: InvoicesDataContextValue = React.useMemo(
      () => ({
        invoices,
        sort,
        handleSortChange: handleSetSort,
        pageSize,
        pageIndex,
        handlePageIndexChange,
        handlePageSizeChange,
      }),
      [
        invoices,
        sort,
        handleSetSort,
        pageSize,
        pageIndex,
        handlePageIndexChange,
        handlePageSizeChange,
      ]
    );

    return (
      <InvoicesDataContext value={contextValue}>
        {children}
      </InvoicesDataContext>
    );
  }
);

InvoicesDataProvider.displayName = 'InvoicesDataProvider';

export { InvoicesDataProvider, InvoicesDataContext };
